// The chrome kit's own copy (components/ui/kit.tsx): accessible names only.
//
// Namespace `kit`; owner: DES1 (design system). Every visible word in a kit
// component comes from its caller; these are the few labels the kit adds for
// screen readers and tooltips. `{count}`, `{done}` and `{total}` are numbers.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  loading: 'Loading',
  othersLabel: 'and {count} others',
  openRunRecord: 'Open the run record',
  progressOf: '{done} of {total}',
};

export type KitCopyKey = keyof typeof en;

export const kitCopy: NamespaceCatalog<KitCopyKey> = {
  bg: {
    loading: 'Зарежда се',
    othersLabel: 'и още {count}',
    openRunRecord: 'Отвори записа на изпълнението',
    progressOf: '{done} от {total}',
  },
  hr: {
    loading: 'Učitavanje',
    othersLabel: 'i još {count}',
    openRunRecord: 'Otvori zapis izvođenja',
    progressOf: '{done} od {total}',
  },
  cs: {
    loading: 'Načítání',
    othersLabel: 'a další {count}',
    openRunRecord: 'Otevřít záznam běhu',
    progressOf: '{done} z {total}',
  },
  da: {
    loading: 'Indlæser',
    othersLabel: 'og {count} andre',
    openRunRecord: 'Åbn kørselsloggen',
    progressOf: '{done} af {total}',
  },
  nl: {
    loading: 'Laden',
    othersLabel: 'en {count} anderen',
    openRunRecord: 'Open het runverslag',
    progressOf: '{done} van {total}',
  },
  en,
  et: {
    loading: 'Laadimine',
    othersLabel: 'ja veel {count}',
    openRunRecord: 'Ava käivituse kirje',
    progressOf: '{done} {total}-st',
  },
  fi: {
    loading: 'Ladataan',
    othersLabel: 'ja {count} muuta',
    openRunRecord: 'Avaa ajon tiedot',
    progressOf: '{done}/{total}',
  },
  fr: {
    loading: 'Chargement',
    othersLabel: 'et {count} autres',
    openRunRecord: 'Ouvrir le journal d’exécution',
    progressOf: '{done} sur {total}',
  },
  de: {
    loading: 'Wird geladen',
    othersLabel: 'und {count} weitere',
    openRunRecord: 'Ausführungsprotokoll öffnen',
    progressOf: '{done} von {total}',
  },
  el: {
    loading: 'Φόρτωση',
    othersLabel: 'και {count} ακόμη',
    openRunRecord: 'Άνοιγμα της καταγραφής εκτέλεσης',
    progressOf: '{done} από {total}',
  },
  hu: {
    loading: 'Betöltés',
    othersLabel: 'és még {count}',
    openRunRecord: 'Futási napló megnyitása',
    progressOf: '{done}/{total}',
  },
  ga: {
    loading: 'Á lódáil',
    othersLabel: 'agus {count} eile',
    openRunRecord: 'Oscail taifead an rite',
    progressOf: '{done} as {total}',
  },
  it: {
    loading: 'Caricamento',
    othersLabel: 'e altri {count}',
    openRunRecord: 'Apri il registro di esecuzione',
    progressOf: '{done} di {total}',
  },
  lv: {
    loading: 'Notiek ielāde',
    othersLabel: 'un vēl {count}',
    openRunRecord: 'Atvērt izpildes ierakstu',
    progressOf: '{done} no {total}',
  },
  lt: {
    loading: 'Įkeliama',
    othersLabel: 'ir dar {count}',
    openRunRecord: 'Atidaryti vykdymo įrašą',
    progressOf: '{done} iš {total}',
  },
  mt: {
    loading: 'Qed jitgħabba',
    othersLabel: 'u {count} oħra',
    openRunRecord: 'Iftaħ ir-rekord tat-tħaddim',
    progressOf: '{done} minn {total}',
  },
  pl: {
    loading: 'Ładowanie',
    othersLabel: 'i jeszcze {count}',
    openRunRecord: 'Otwórz zapis uruchomienia',
    progressOf: '{done} z {total}',
  },
  pt: {
    loading: 'A carregar',
    othersLabel: 'e mais {count}',
    openRunRecord: 'Abrir o registo da execução',
    progressOf: '{done} de {total}',
  },
  ro: {
    loading: 'Se încarcă',
    othersLabel: 'și încă {count}',
    openRunRecord: 'Deschide înregistrarea rulării',
    progressOf: '{done} din {total}',
  },
  sk: {
    loading: 'Načítava sa',
    othersLabel: 'a ďalší {count}',
    openRunRecord: 'Otvoriť záznam behu',
    progressOf: '{done} z {total}',
  },
  sl: {
    loading: 'Nalaganje',
    othersLabel: 'in še {count}',
    openRunRecord: 'Odpri zapis izvajanja',
    progressOf: '{done} od {total}',
  },
  es: {
    loading: 'Cargando',
    othersLabel: 'y {count} más',
    openRunRecord: 'Abrir el registro de la ejecución',
    progressOf: '{done} de {total}',
  },
  sv: {
    loading: 'Laddar',
    othersLabel: 'och {count} till',
    openRunRecord: 'Öppna körningsloggen',
    progressOf: '{done} av {total}',
  },
};

export default kitCopy;
